// Lifetime stats — pure module.
//
// SPEC §7.1 names `played`, `solved`, `currentStreak`, `maxStreak`, and
// `flickDistribution`. Phase 6 ships those plus the Sunday-streak pair and an
// undo-bucket distribution (Letter Flick's analog of Wordle's guess histogram).
//
// Pure: no DOM, no localStorage, no console. Every mutation returns a NEW
// LifetimeStats object so callers can freely diff old vs new for animations
// (Phase 7+) without spotting an aliased reference.
//
// Streak semantics (SPEC §7.2):
//   - Increment on a consecutive daily solve (yesterday → today, dayOffset
//     diff === 1).
//   - Break on a loss OR a missed day (diff > 1 since last successful solve).
//   - Re-attempting the same day (diff === 0 or negative — clock skew, undo
//     into a re-completion of the same puzzle, etc.) is a defensive no-op:
//     we never "lose" a streak from a same-day repeat and we never inflate
//     one. Phase 6 guarantees one stats update per (puzzleId, terminal status)
//     via the source discriminator, so diff === 0 only happens for genuine
//     clock-skew edge cases.
//
// Sunday streak: a weekly-cadence streak that only advances when today is a
// local-Sunday AND the previous successful Sunday was exactly 7 days ago.
// `lastCompletedSundayOffset` updates only on wins on Sundays — losses
// don't reset it (the field tracks "last successful Sunday"; the streak
// counter `sundayStreak` is what resets on a Sunday loss).

import type { GameEvent } from './events'

export interface LifetimeStats {
  readonly played: number
  readonly solved: number
  readonly currentStreak: number
  readonly maxStreak: number
  readonly sundayStreak: number
  readonly maxSundayStreak: number
  readonly lastCompletedDayOffset: number | null
  readonly lastCompletedSundayOffset: number | null
  // Keys are stringified small integers "0".."MAX_UNDOS" (see state.ts).
  // Stored as Record (not Map) so JSON round-trips natively and the validator
  // can narrow without hand-walking entries.
  readonly undoDistribution: Readonly<Record<string, number>>
}

export function emptyLifetimeStats(): LifetimeStats {
  return {
    played: 0,
    solved: 0,
    currentStreak: 0,
    maxStreak: 0,
    sundayStreak: 0,
    maxSundayStreak: 0,
    lastCompletedDayOffset: null,
    lastCompletedSundayOffset: null,
    undoDistribution: {},
  }
}

// Local-time Sunday detection. JS Date.getDay() returns 0 for Sunday, 1..6
// for Mon..Sat. Local-time matches our day-boundary convention in daily.ts.
export function isSunday(now: Date = new Date()): boolean {
  return now.getDay() === 0
}

// Win % rounded to a whole percentage. Returns null when no games have been
// played so the renderer can omit the field entirely (vs displaying "0%").
export function computeWinPercentage(stats: LifetimeStats): number | null {
  if (stats.played === 0) return null
  return Math.round((stats.solved / stats.played) * 100)
}

// Gross flick count derived from the event log. Counts ALL `flick` events,
// including phantom flicks subsequently undone — the player still executed
// the action, and the modal tally "You: 6 flicks · 2 undos" should report
// the effort, not the net surviving moves. `state.flickCount` is also gross
// (see state.ts: incremented in applyFlick, not decremented on undo) and
// would give the same number; deriving from the log keeps the share-grid's
// authoritative event stream as the single source of truth for tally text.
export function computeGrossFlickCount(log: readonly GameEvent[]): number {
  let n = 0
  for (const e of log) {
    if (e.type === 'flick') n += 1
  }
  return n
}

// Pure transition function. Returns a NEW LifetimeStats object reflecting
// the outcome of the just-completed game. `todayDayOffset` is the day index
// (from daily.ts's getDayOffset) of the puzzle that was just completed;
// `now` is injectable so unit tests can pin Sundays without freezing the
// clock module-wide.
export function updateStatsOnGameEnd(
  prev: LifetimeStats,
  outcome: { readonly won: boolean; readonly undoCount: number },
  todayDayOffset: number,
  now: Date = new Date(),
): LifetimeStats {
  const sunday = isSunday(now)
  let played = prev.played + 1
  let solved = prev.solved
  let currentStreak = prev.currentStreak
  let maxStreak = prev.maxStreak
  let sundayStreak = prev.sundayStreak
  let maxSundayStreak = prev.maxSundayStreak
  let lastCompletedDayOffset: number | null = todayDayOffset
  let lastCompletedSundayOffset: number | null = prev.lastCompletedSundayOffset
  const undoDistribution: Record<string, number> = { ...prev.undoDistribution }

  if (outcome.won) {
    solved += 1
    // Undo-bucket: keyed by integer count cast to string. We don't clamp to
    // MAX_UNDOS here; the caller passes state.undoCount which is bounded by
    // the controller already, and a hypothetical future undo-cap raise would
    // silently widen the histogram instead of corrupting it.
    const key = String(outcome.undoCount)
    undoDistribution[key] = (undoDistribution[key] ?? 0) + 1

    // Daily streak
    if (prev.lastCompletedDayOffset === null) {
      currentStreak = 1
    } else {
      const diff = todayDayOffset - prev.lastCompletedDayOffset
      if (diff === 1) {
        currentStreak += 1
      } else if (diff > 1) {
        // Missed at least one day — reset to 1 (today still counts as a solve).
        currentStreak = 1
      }
      // diff <= 0: same day or earlier (clock skew / repeat). No-op.
    }
    if (currentStreak > maxStreak) maxStreak = currentStreak

    // Sunday streak — only when today is Sunday
    if (sunday) {
      if (prev.lastCompletedSundayOffset === null) {
        sundayStreak = 1
      } else {
        const sdiff = todayDayOffset - prev.lastCompletedSundayOffset
        if (sdiff === 7) {
          sundayStreak += 1
        } else if (sdiff > 7) {
          sundayStreak = 1
        }
        // sdiff <= 0 or other non-7 positive (e.g. 14 if a Sunday was missed):
        // the > 7 branch above resets to 1; <= 0 is defensive no-op.
      }
      if (sundayStreak > maxSundayStreak) maxSundayStreak = sundayStreak
      lastCompletedSundayOffset = todayDayOffset
    }
  } else {
    // Loss: reset the daily streak. Sunday streak also resets only when
    // today is itself a Sunday (a Tuesday loss shouldn't kill a Sunday
    // streak that's already counted).
    currentStreak = 0
    if (sunday) sundayStreak = 0
    // lastCompletedSundayOffset NOT updated on losses — it tracks the last
    // SUCCESSFUL Sunday so a Sun-loss → following-Sun-win still computes the
    // right sdiff (7) to continue or start a streak.
  }

  return {
    played,
    solved,
    currentStreak,
    maxStreak,
    sundayStreak,
    maxSundayStreak,
    lastCompletedDayOffset,
    lastCompletedSundayOffset,
    undoDistribution,
  }
}

// Runtime narrowing guard. Used by persistence.ts when re-hydrating
// PersistedState v2; the load path treats any malformed stats blob as
// "no stats" by returning false here, prompting the loader to fall back
// to emptyLifetimeStats().
export function isValidLifetimeStats(v: unknown): v is LifetimeStats {
  if (v === null || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  if (!isNonNegInt(o['played'])) return false
  if (!isNonNegInt(o['solved'])) return false
  if (!isNonNegInt(o['currentStreak'])) return false
  if (!isNonNegInt(o['maxStreak'])) return false
  if (!isNonNegInt(o['sundayStreak'])) return false
  if (!isNonNegInt(o['maxSundayStreak'])) return false
  if (!isOptionalInt(o['lastCompletedDayOffset'])) return false
  if (!isOptionalInt(o['lastCompletedSundayOffset'])) return false
  if (!isUndoDistribution(o['undoDistribution'])) return false
  return true
}

function isNonNegInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && Number.isInteger(v)
}

function isOptionalInt(v: unknown): v is number | null {
  if (v === null) return true
  return typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v)
}

function isUndoDistribution(v: unknown): v is Record<string, number> {
  if (v === null || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  for (const k of Object.keys(o)) {
    const n = o[k]
    if (!isNonNegInt(n)) return false
  }
  return true
}
